import { create } from 'zustand';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  action_url: string | null;
  action_label: string | null;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
}

interface NotificationStore {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: Error | null;

  fetchNotifications: (options?: { unreadOnly?: boolean }) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshAll: () => Promise<void>; // 알림 목록과 읽지 않은 수 모두 갱신
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,

  fetchNotifications: async (options) => {
    set({ loading: true, error: null });
    try {
      const { unreadOnly = false } = options || {};
      const queryParams = new URLSearchParams();
      if (unreadOnly) {
        queryParams.append('unread', 'true');
      }

      const response = await fetch(`/api/v1/notifications?${queryParams.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('알림 목록을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      set({ notifications: data.notifications || [], loading: false });
    } catch (error) {
      set({ error: error as Error, loading: false });
      console.error('Failed to fetch notifications:', error);
    }
  },

  fetchUnreadCount: async () => {
    try {
      const response = await fetch('/api/v1/notifications/unread-count', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('읽지 않은 알림 수를 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      set({ unreadCount: data.count || 0 });
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  },

  markAsRead: async (id) => {
    try {
      const response = await fetch(`/api/v1/notifications/${id}/read`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('알림 읽음 처리에 실패했습니다.');
      }

      // 로컬 상태 업데이트
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        ),
      }));

      // 읽지 않은 수 갱신
      await get().fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
      throw error;
    }
  },

  markAllAsRead: async () => {
    try {
      const response = await fetch('/api/v1/notifications/read-all', {
        method: 'PUT',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('모든 알림 읽음 처리에 실패했습니다.');
      }

      // 로컬 상태 업데이트
      const now = new Date().toISOString();
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          is_read: true,
          read_at: now,
        })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      throw error;
    }
  },

  refreshAll: async () => {
    // 알림 목록과 읽지 않은 수를 동시에 갱신
    await Promise.all([
      get().fetchNotifications(),
      get().fetchUnreadCount(),
    ]);
  },
}));
